'use client';

import type React from 'react';
import { useCallback, useEffect } from 'react';
import { scheduleFavoriteConfetti } from '@/src/components/features/listings/FavoriteConfetti/scheduleFavoriteConfetti';
import { analytics } from '@/src/components/features/property/lib';
import { useSavedListings } from '@/src/components/features/property/contexts/SavedListingsContext';
import { useIsListingSaved, useOptionalListingActionContext } from '@/src/components/features/listing-actions';
import { useCurrentUser } from '@/src/lib/hooks/query';
import {
  buildSavedListing,
  DEFAULT_LISTING_ACTION_ANALYTICS,
  getListingActionClickstreamListing,
  getListingActionId,
  type ListingActionAnalyticsContext,
  type ListingLike,
} from '@/src/components/features/listing-actions/listingActionUtils';

export type ListingSaveActions = {
  listingSaved: boolean;
  savePending: boolean;
  toggleSave: () => void;
  identityResolved: boolean;
};

export type ListingSaveAnalyticsContext = ListingActionAnalyticsContext;

/**
 * Toggles the listing's saved state through SavedListingsContext.
 *
 * Legacy HALO dispatched UserItemActions.toggleUserItem('favorite',
 * listing) through Redux. We write to sessionStorage via
 * SavedListingsContext instead until authenticated server-side favorites
 * port.
 */
export function useListingSaveActions(
  listing: ListingLike | null | undefined,
  buttonRef?: React.RefObject<HTMLButtonElement | null>,
  analyticsContext: ListingSaveAnalyticsContext = DEFAULT_LISTING_ACTION_ANALYTICS,
): ListingSaveActions {
  const listingActionContext = useOptionalListingActionContext();
  const { data: currentUser } = useCurrentUser();
  const identityResolved = currentUser !== undefined;
  const { isSaved, saveListing, removeListing } = useSavedListings();

  const listingId = getListingActionId(listing);
  const listingSaved = useIsListingSaved(listingActionContext, listing, listingId ? isSaved(listingId) : false);
  const savePending = listingActionContext?.isListingSavePending(listing) ?? false;

  useEffect(() => {
    listingActionContext?.reconcileFavoriteOverride(listing);
  }, [listing, listingActionContext]);

  const toggleSave = useCallback(() => {
    const favoriteButton = buttonRef?.current;
    const shouldCelebrate =
      Boolean(currentUser?.loggedIn) && Boolean(listing && listingId) && !listingSaved && !savePending;

    if (listingActionContext) {
      void listingActionContext.toggleSaveListing(listing, analyticsContext).then((interactionAccepted) => {
        if (interactionAccepted && shouldCelebrate && favoriteButton) scheduleFavoriteConfetti(favoriteButton);
      });
      return;
    }

    if (!identityResolved) return;
    if (!listing || !listingId) return;
    const clickstreamListing = getListingActionClickstreamListing(listing);

    if (listingSaved) {
      analytics.emitTrackUnSaveHomeClickEvent({ ...analyticsContext, listing: clickstreamListing });
      removeListing(listingId);
      return;
    }

    analytics.emitTrackSaveHomeClickEvent({ ...analyticsContext, listing: clickstreamListing });
    const saved = buildSavedListing(listing);
    if (saved) {
      saveListing(saved);
      if (shouldCelebrate && favoriteButton) scheduleFavoriteConfetti(favoriteButton);
    }
  }, [
    analyticsContext,
    buttonRef,
    currentUser?.loggedIn,
    identityResolved,
    listing,
    listingActionContext,
    listingId,
    listingSaved,
    removeListing,
    savePending,
    saveListing,
  ]);

  return { listingSaved, savePending, toggleSave, identityResolved };
}
